/**
 * The discrete sets everything in the animation system is snapped onto.
 *
 * This is the backbone of the design, not an optimisation. The player's continuous
 * situation is repeatedly rounded onto small finite sets so that a finite clip library can
 * always cover it: four speeds, five body directions, eleven travel directions, thirty-four
 * "manoeuvre" buckets. See docs/original-engine/animation-system.md §4.
 *
 * Every value here was read out of the original rather than taken from the specification
 * document, and each carries the file:line it came from.
 */

import { Vector3 } from 'three';
import { rangeVelocity } from './velocity';

export { Velocity, rangeVelocity, floorVelocity, clampVelocity, floatToEnumVelocity, enumToFloatVelocity, velocityID } from './velocity';

const FORWARD = new Vector3(0, -1, 0);

/** `Vector3::GetRotated2D` — used here only to build the constant tables. */
function rotatedForward(radians: number): Vector3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  // FORWARD is (0,-1,0): x' = -0*sin... spelled out rather than special-cased.
  return new Vector3(FORWARD.x * cos - FORWARD.y * sin, FORWARD.y * cos + FORWARD.x * sin, 0);
}

// ---------------------------------------------------------------------------
// 4.3  Allowed body directions — humanoidbase.cpp:63-73
// ---------------------------------------------------------------------------

/**
 * The five directions a body is ever modelled as facing, relative to travel.
 *
 * Note the absence of +-90 degrees: a player is never considered to be running exactly
 * sideways. Anything close to sideways snaps to 45 or 135.
 */
export const ALLOWED_BODY_ANGLES: readonly number[] = [
  0,
  0.25 * Math.PI,
  -0.25 * Math.PI,
  0.75 * Math.PI,
  -0.75 * Math.PI,
];

export const ALLOWED_BODY_DIRECTIONS: readonly Vector3[] = [
  rotatedForward(0),
  rotatedForward(-0.25 * Math.PI),
  rotatedForward(0.25 * Math.PI),
  rotatedForward(-0.75 * Math.PI),
  rotatedForward(0.75 * Math.PI),
];

// ---------------------------------------------------------------------------
// 4.4  Preferred travel directions — humanoidbase.cpp:75-97
// ---------------------------------------------------------------------------

/** Eleven headings a clip's outgoing direction is snapped to before it is scored. */
export const PREFERRED_ANGLES: readonly number[] = [
  0,
  0.111 * Math.PI,
  -0.111 * Math.PI,
  0.25 * Math.PI,
  -0.25 * Math.PI,
  0.5 * Math.PI,
  -0.5 * Math.PI,
  0.75 * Math.PI,
  -0.75 * Math.PI,
  0.999 * Math.PI,
  -0.999 * Math.PI,
];

export const PREFERRED_DIRECTIONS: readonly Vector3[] = PREFERRED_ANGLES.map(rotatedForward);

/**
 * Snap to the allowed direction with the largest dot product (humanoidbase.cpp:2547-2561).
 *
 * The original seeds its best-so-far with -1.0, so a vector pointing exactly opposite to
 * every candidate would keep index 0. That cannot happen with these tables, but the seed is
 * reproduced rather than replaced with -Infinity so the two read the same.
 */
function snapByDot(table: readonly Vector3[], source: Vector3): Vector3 {
  let bestDot = -1;
  let best = table[0]!;
  for (const candidate of table) {
    const dot = candidate.dot(source);
    if (dot > bestDot) {
      bestDot = dot;
      best = candidate;
    }
  }
  return best;
}

/**
 * Snap to the allowed angle with the smallest difference (humanoidbase.cpp:2563-2576).
 *
 * BUG, DELIBERATELY KEPT. The original compares `fabs(candidate - angle)` — a RAW
 * difference, not a wrapped one — so an input just below -pi is judged far from the
 * candidate just below +pi even though they are neighbours. Both tables stop at +-0.999pi
 * and +-0.75pi, and every caller passes an angle already wrapped into (-pi, pi], so the
 * two agree everywhere the game actually reaches. Kept as written rather than "fixed",
 * because a fix here would silently change which clip wins near a full turn-around.
 */
function snapByAngle(table: readonly number[], angle: number): number {
  let bestDiff = 10000;
  let best = table[0]!;
  for (const candidate of table) {
    const diff = Math.abs(candidate - angle);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = candidate;
    }
  }
  return best;
}

export const forceIntoAllowedBodyDirectionVec = (v: Vector3): Vector3 =>
  snapByDot(ALLOWED_BODY_DIRECTIONS, v);
export const forceIntoAllowedBodyDirectionAngle = (a: number): number =>
  snapByAngle(ALLOWED_BODY_ANGLES, a);
export const forceIntoPreferredDirectionVec = (v: Vector3): Vector3 =>
  snapByDot(PREFERRED_DIRECTIONS, v);
export const forceIntoPreferredDirectionAngle = (a: number): number =>
  snapByAngle(PREFERRED_ANGLES, a);

// ---------------------------------------------------------------------------
// 4.2  Quadrants — animcollection.cpp:61-106
// ---------------------------------------------------------------------------

export interface Quadrant {
  id: number;
  velocity: number;
  angle: number;
  position: Vector3;
  /** See the note on quadrants 11, 22 and 33 below. */
  reachable: boolean;
}

const QUADRANT_ANGLES_DEG: readonly (number | null)[] = [
  0,
  20,
  45,
  90,
  135,
  179,
  -20,
  -45,
  -90,
  -135,
  // angleID 10 would be -179, but the original's line is commented out
  // (animcollection.cpp:94). See QUADRANTS below.
  null,
];

/**
 * The thirty-four buckets a clip's outgoing movement can fall into. Quadrant identity is
 * the primary "is this the same manoeuvre?" test in the selector, so the numbering matters:
 * clips are compared by quadrant id for equality.
 *
 * BUG, HANDLED RATHER THAN REPRODUCED. In the original, `angleID == 10` falls through every
 * branch and leaves `radian angle;` uninitialised (animcollection.cpp:83-94) — the -179
 * degree line is commented out. Quadrants 11, 22 and 33 therefore have an undefined angle
 * and position in the shipped binary. Undefined behaviour cannot be reproduced faithfully.
 *
 * What is done instead: those three are built as exact DUPLICATES of the entry before them
 * (-135 degrees at the same speed), which is what the uninitialised slot almost certainly
 * held — it is the same stack slot the previous iteration wrote — and they are additionally
 * marked unreachable. Either mechanism alone would already keep them from ever being chosen,
 * because the nearest-quadrant search keeps the FIRST of equal distances, so the real -135
 * bucket always wins the tie. Every other id stays numerically identical to the original,
 * which matters because clips are compared by quadrant id for equality.
 *
 * Do NOT "complete" the table with -179 degrees. That looks like the author's intent, but it
 * would make these three winnable and would split hard left turns from hard right turns that
 * the original lumps together — a change to which clips are considered the same manoeuvre.
 */
export const QUADRANTS: readonly Quadrant[] = buildQuadrants();

function buildQuadrants(): Quadrant[] {
  const list: Quadrant[] = [
    { id: 0, velocity: 0, angle: 0, position: new Vector3(0, 0, 0), reachable: true },
  ];

  let id = 1;
  // Dribble, Walk, Sprint — in that order, ids 1-11, 12-22, 23-33.
  for (const velocity of [1, 2, 3]) {
    const speed = velocity === 1 ? 3.5 : velocity === 2 ? 5.0 : 8.0;
    let previousAngle = 0;
    for (const degrees of QUADRANT_ANGLES_DEG) {
      // degrees === null is the uninitialised slot: reuse what the C++ stack slot held.
      const angle = degrees === null ? previousAngle : (Math.PI / 180) * degrees;
      list.push({
        id,
        velocity,
        angle,
        position: rotatedForward(angle).multiplyScalar(speed),
        reachable: degrees !== null,
      });
      previousAngle = angle;
      id++;
    }
  }
  return list;
}

/**
 * The quadrant a movement vector belongs to (animcollection.cpp:864-879).
 *
 * The movement is first snapped to its velocity bucket, so only its direction and its
 * bucket matter, then the nearest quadrant position wins by plain Euclidean distance.
 * The C++ signature also takes an angle; it is unused.
 */
export function getQuadrantID(movement: Vector3): number {
  const length = movement.length();
  const speed = rangeVelocity(length);
  const adapted =
    length === 0 ? new Vector3(0, 0, 0) : movement.clone().multiplyScalar(speed / length);

  let bestId = 0;
  let bestDistance = 100000;
  for (const quadrant of QUADRANTS) {
    if (!quadrant.reachable) continue;
    const distance = adapted.distanceTo(quadrant.position);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = quadrant.id;
    }
  }
  return bestId;
}

export function quadrantById(id: number): Quadrant {
  const quadrant = QUADRANTS[id];
  if (!quadrant) throw new Error(`No animation quadrant ${id}`);
  return quadrant;
}
